import React, { useContext, useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Avatar, 
  Chip, 
  IconButton,
  Menu,
  MenuItem,
  Divider as MuiDivider,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import BusinessIcon from '@mui/icons-material/Business';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LogoutIcon from '@mui/icons-material/Logout';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PeopleIcon from '@mui/icons-material/People';
import MenuIcon from '@mui/icons-material/Menu';

const NavBar = () => {
  const { user, logout, canManageUsers } = useContext(AuthContext);
  const { companyId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/login');
  };

  const handleUsersClick = () => {
    handleMenuClose();
    navigate('/users');
  };

  const handleCompanySwitchClick = () => {
    handleMenuClose();
    navigate('/');
  };

  const getCompanyDisplayName = (id) => {
    // This could be enhanced to fetch from a companies cache
    return `Company ${id.slice(-6)}`;
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      'super_admin': 'Super Admin',
      'admin': 'Admin',
      'manager': 'Manager',
      'associate': 'Associate'
    };
    return roleNames[role] || role;
  };

  const getRoleColor = (role) => {
    const roleColors = {
      'super_admin': 'error',
      'admin': 'warning',
      'manager': 'info',
      'associate': 'success'
    };
    return roleColors[role] || 'default';
  };

  return (
    <AppBar position="static" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <Toolbar sx={{ minHeight: isMobile ? 56 : 64 }}>
        <Box 
          display="flex" 
          alignItems="center" 
          gap={1} 
          component={Link}
          to="/"
          sx={{ 
            flexGrow: 1, 
            color: 'inherit', 
            textDecoration: 'none',
            '&:hover': { textDecoration: 'none' }
          }}
        >
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', width: isMobile ? 28 : 32, height: isMobile ? 28 : 32 }}>
            <AccountTreeIcon fontSize="small" />
          </Avatar>
          <Typography variant={isMobile ? "subtitle1" : "h6"} component="div" fontWeight="bold" noWrap>
            NeoShift.ai
          </Typography>
          <Chip 
            label="Beta" 
            size="small" 
            sx={{ 
              bgcolor: 'rgba(255,255,255,0.2)', 
              color: 'white',
              fontSize: '0.7rem',
              height: isMobile ? 18 : 20
            }}
          />
        </Box>
        {user && (
          <>
            {isMobile ? (
              /* Mobile: Show menu icon */
              <>
                <IconButton
                  color="inherit"
                  onClick={handleMenuOpen}
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.2)'
                    }
                  }}
                >
                  <MenuIcon />
                </IconButton>
                
                <Menu
                  anchorEl={anchorEl}
                  open={menuOpen}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      minWidth: 250,
                      boxShadow: 3
                    }
                  }}
                >
                  {/* User Info */}
                  <Box sx={{ px: 2, py: 1.5, bgcolor: 'rgba(103, 126, 234, 0.08)' }}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                        {(user.firstName?.[0] || user.email[0]).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email}
                        </Typography>
                        {user.role && (
                          <Chip 
                            label={getRoleDisplayName(user.role)}
                            size="small"
                            color={getRoleColor(user.role)}
                            sx={{ 
                              height: 18,
                              fontSize: '0.65rem',
                              '& .MuiChip-label': { px: 1 }
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                  
                  <MuiDivider />
                  
                  {/* User Management */}
                  {canManageUsers && canManageUsers() && (
                    <MenuItem onClick={handleUsersClick}>
                      <ListItemIcon>
                        <PeopleIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Users</ListItemText>
                    </MenuItem>
                  )}
                  
                  {/* Company Switcher */}
                  {companyId && (
                    <MenuItem onClick={handleCompanySwitchClick}>
                      <ListItemIcon>
                        <SwapHorizIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Switch Company</ListItemText>
                    </MenuItem>
                  )}
                  
                  <MuiDivider />
                  
                  {/* Logout */}
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Logout</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            ) : (
              /* Desktop: Show all buttons inline */
              <Box display="flex" alignItems="center" gap={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', width: 32, height: 32 }}>
                    {(user.firstName?.[0] || user.email[0]).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.2 }}>
                      {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email}
                    </Typography>
                    {user.role && (
                      <Chip 
                        label={getRoleDisplayName(user.role)}
                        size="small"
                        color={getRoleColor(user.role)}
                        sx={{ 
                          height: 16,
                          fontSize: '0.6rem',
                          '& .MuiChip-label': { px: 1 }
                        }}
                      />
                    )}
                  </Box>
                </Box>
                
                {/* User Management Button */}
                {canManageUsers && canManageUsers() && (
                  <Button
                    color="inherit"
                    onClick={() => navigate('/users')}
                    startIcon={<PeopleIcon />}
                    size="small"
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.1)',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.2)'
                      },
                      mr: 1
                    }}
                  >
                    Users
                  </Button>
                )}
                
                {/* Company Switcher */}
                {companyId && (
                  <IconButton
                    color="inherit"
                    onClick={() => navigate('/')}
                    size="small"
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.1)',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.2)'
                      }
                    }}
                    title="Switch Company"
                  >
                    <SwapHorizIcon fontSize="small" />
                  </IconButton>
                )}
                
                <Button 
                  color="inherit" 
                  onClick={handleLogout}
                  startIcon={<LogoutIcon />}
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.2)'
                    }
                  }}
                >
                  Logout
                </Button>
              </Box>
            )}
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default NavBar; 